import { AlertTriangle, ExternalLink, GitPullRequest } from "lucide-react";
import { PullRequestReviewLink } from "@/components/pull-requests/pull-request-review-link";
import { DeployRecordSummary } from "@/components/pull-requests/deploy-record-summary";
import { cn } from "@/lib/utils";
import type { Epic } from "@/lib/api/types";
import { destructiveWrapUpMigrations, wrapUpFindings, type EpicDetail, type EpicWrapUpView } from "@/lib/api/epics";

const STATE_LABELS = {
  open: "In review",
  merged: "Merged",
  closed: "Pull request closed without merging",
} as const;

const STATE_COLORS = {
  open: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  merged: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  closed: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
} as const;

const DRAFT_COLOR = "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
const READY_COLOR = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";

/**
 * How an open pull request reads once wrap-up has run (ENG-329): a draft with
 * the reason wrap-up kept it there, or "Ready for you". Null when wrap-up has
 * nothing to add (not run yet, still running, or a follow-up build is open).
 */
export function openPullRequestWrapUp(
  wrapUp: Pick<EpicWrapUpView, "wrapUp" | "displayState"> | null | undefined
): { label: string; color: string; reason: string | null } | null {
  if (!wrapUp?.wrapUp) return null;
  switch (wrapUp.displayState) {
    case "ready":
      return { label: "Ready for you", color: READY_COLOR, reason: null };
    case "needs_decision":
    case "failed": {
      const reason =
        wrapUpFindings(wrapUp.wrapUp)[0] ??
        (wrapUp.displayState === "failed"
          ? "Wrap-up failed."
          : "Wrap-up needs a decision from you.");
      return { label: "Draft", color: DRAFT_COLOR, reason };
    }
    default:
      return null;
  }
}

/**
 * Where the epic's build stands on GitHub: "In review" with a link while its
 * pull request is open (the epic only becomes implemented once it is merged),
 * then "Merged", or "closed without merging". Renders nothing without a PR.
 *
 * With the epic's wrap-up (ENG-329), an open PR reads "Draft" with the reason
 * or "Ready for you", and a destructive migration that must be confirmed
 * before merging is called out with a link to the review page.
 *
 * ENG-421: the epic's newest deploy record (status, migrations applied,
 * backup, commit) is shown under the pull request.
 */
export function EpicPullRequestStatus({
  epic,
  wrapUp,
}: {
  epic: Pick<Epic, "buildPullRequest" | "convertedFromRequests"> & Pick<EpicDetail, "deployRecords">;
  wrapUp?: EpicWrapUpView | null | undefined;
}) {
  const pr = epic.buildPullRequest;
  if (!pr) return null;
  const epicRequestId = epic.convertedFromRequests?.[0]?.id;
  const open = pr.state === "open";
  const wrapUpState = open ? openPullRequestWrapUp(wrapUp) : null;
  const destructive = open ? destructiveWrapUpMigrations(wrapUp?.wrapUp) : [];
  return (
    <div className="space-y-2 text-sm" data-testid="epic-pull-request-status">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
            wrapUpState ? wrapUpState.color : STATE_COLORS[pr.state]
          )}
        >
          <GitPullRequest className="h-3.5 w-3.5" />
          {wrapUpState ? wrapUpState.label : STATE_LABELS[pr.state]}
        </span>
        {pr.state === "open" && epicRequestId && (
          <PullRequestReviewLink epicRequestId={epicRequestId} />
        )}
        <a
          href={pr.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          View on GitHub
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {wrapUpState?.reason && (
        <p className="text-muted-foreground" data-testid="wrap-up-draft-reason">
          {wrapUpState.reason}
        </p>
      )}
      {destructive.length > 0 && (
        <div
          role="alert"
          data-testid="destructive-migration-warning"
          className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-500" aria-hidden />
          <div className="space-y-1">
            <p className="font-medium">
              {destructive.length === 1
                ? "This build includes a destructive migration"
                : `This build includes ${String(destructive.length)} destructive migrations`}
            </p>
            <ul className="list-disc pl-5 text-muted-foreground">
              {destructive.map((m) => (
                <li key={m.path}>
                  <code className="text-xs">{m.path}</code>
                  {m.reasons.length > 0 && <>: {m.reasons.join("; ")}</>}
                </li>
              ))}
            </ul>
            <p>Back up the live database, then tick “I have backed up the live database” in the merge dialog.</p>
            {epicRequestId ? (
              <PullRequestReviewLink epicRequestId={epicRequestId} />
            ) : (
              <a
                href={pr.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                Open the pull request
              </a>
            )}
          </div>
        </div>
      )}
      <DeployRecordSummary records={epic.deployRecords} />
    </div>
  );
}
